import { LinearSolver, SparseCsr } from './LinearSolver';
import { CholeskySolver } from './CholeskySolver';
import { fitRotation } from './svd3';

export interface ArapSolveOptions {
  /** Number of local/global ARAP iterations per load step. */
  arapIters?: number;
  /** Incremental constraint-loading sub-steps (rest → target). Higher = more robust
   *  for very large single-shot rotations, more cost. Default 1. */
  loadSteps?: number;
}

/** Numeric-only mesh topology (typed arrays, no Vertex refs) — see {@link MeshTopology}. */
export interface ArapNumericTopology {
  n: number;
  rowPtr: Int32Array;
  colIdx: Int32Array;
  weight: Float64Array;
  diag: Float64Array;
  restPos: Float64Array;
}

/** A fully serializable ARAP problem (transferable to a worker). */
export interface ArapProblem extends ArapNumericTopology {
  constrainedMask: Uint8Array;
  regularization?: number;
  useRotations?: boolean;
}

/**
 * Pure-numeric ARAP solver core (Sorkine & Alexa 2007). Operates purely on integer
 * indices and typed arrays — no `Vertex` references — so it runs unchanged on the main
 * thread or inside a Web Worker. {@link ArapDeformer} is the `Vertex`-aware adapter.
 *
 * Constraints are imposed by writing the target positions into {@link pos} for the
 * constrained indices, then calling {@link solve}; free positions are read back from
 * {@link pos} (or {@link writeFreePositions}). The cotangent block `L_ff` is constant,
 * so it is factorized once in {@link bind} and reused by every solve.
 */
export default class ArapCore {
  private topo: ArapNumericTopology | null = null;
  private readonly solver: LinearSolver;

  public useRotations = true;

  private isConstrained: Uint8Array = new Uint8Array(0);
  private freeListArr: Int32Array = new Int32Array(0); // freeIdx -> global
  private freeIndexOf: Int32Array = new Int32Array(0); // global -> freeIdx | -1
  private constrainedList: Int32Array = new Int32Array(0); // global indices, ascending

  public pos: Float64Array = new Float64Array(0); // 3n deformed positions
  private rot: Float64Array = new Float64Array(0); // 9n per-vertex rotations
  private readonly cov = new Float64Array(9);

  private rhsX: Float64Array = new Float64Array(0);
  private rhsY: Float64Array = new Float64Array(0);
  private rhsZ: Float64Array = new Float64Array(0);
  private solX: Float64Array = new Float64Array(0);
  private solY: Float64Array = new Float64Array(0);
  private solZ: Float64Array = new Float64Array(0);

  // Constraint-target capture scratch (filled at the start of each solve).
  private cTargetX: Float64Array = new Float64Array(0);
  private cTargetY: Float64Array = new Float64Array(0);
  private cTargetZ: Float64Array = new Float64Array(0);

  constructor(solver?: LinearSolver) {
    this.solver = solver ?? new CholeskySolver();
  }

  public get isBound(): boolean {
    return this.topo !== null;
  }
  public get freeList(): Int32Array {
    return this.freeListArr;
  }
  public get constrainedIndices(): Int32Array {
    return this.constrainedList;
  }

  public bind(problem: ArapProblem): void {
    const { n, rowPtr, colIdx, weight, diag, restPos, constrainedMask } = problem;
    this.topo = { n, rowPtr, colIdx, weight, diag, restPos };
    this.useRotations = problem.useRotations ?? true;

    this.isConstrained = constrainedMask;
    this.freeIndexOf = new Int32Array(n).fill(-1);
    const free: number[] = [];
    const constrained: number[] = [];
    for (let i = 0; i < n; i++) {
      if (constrainedMask[i] === 0) {
        this.freeIndexOf[i] = free.length;
        free.push(i);
      } else {
        constrained.push(i);
      }
    }
    this.freeListArr = Int32Array.from(free);
    this.constrainedList = Int32Array.from(constrained);
    const nFree = this.freeListArr.length;

    this.pos = restPos.slice();
    this.rot = new Float64Array(9 * n);
    this.setAllIdentity();

    this.rhsX = new Float64Array(nFree);
    this.rhsY = new Float64Array(nFree);
    this.rhsZ = new Float64Array(nFree);
    this.solX = new Float64Array(nFree);
    this.solY = new Float64Array(nFree);
    this.solZ = new Float64Array(nFree);
    this.cTargetX = new Float64Array(this.constrainedList.length);
    this.cTargetY = new Float64Array(this.constrainedList.length);
    this.cTargetZ = new Float64Array(this.constrainedList.length);

    this.solver.prefactor(this.buildFreeFreeMatrix(problem.regularization ?? 1e-7));
  }

  /** Resets the free interior to rest (drops warm-start). */
  public resetWarmStart(): void {
    if (this.topo) this.pos.set(this.topo.restPos);
  }

  /** Loads the constrained target positions from a compact `3·nConstrained` array. */
  public loadConstrained(values: Float64Array): void {
    const cl = this.constrainedList;
    for (let c = 0; c < cl.length; c++) {
      const i = cl[c];
      this.pos[3 * i] = values[3 * c];
      this.pos[3 * i + 1] = values[3 * c + 1];
      this.pos[3 * i + 2] = values[3 * c + 2];
    }
  }

  /** Copies the solved free positions into `out` (length `3·nFree`, free-list order). */
  public writeFreePositions(out: Float64Array): void {
    const fl = this.freeListArr;
    for (let fi = 0; fi < fl.length; fi++) {
      const gi = fl[fi];
      out[3 * fi] = this.pos[3 * gi];
      out[3 * fi + 1] = this.pos[3 * gi + 1];
      out[3 * fi + 2] = this.pos[3 * gi + 2];
    }
  }

  /**
   * Relaxes the free interior to match the constraint targets currently stored in
   * {@link pos} (constrained entries). Restarts rotations and seeds with a harmonic
   * (R = I) solve to stay in the rigid basin; free entries keep their warm-start.
   */
  public solve(options: ArapSolveOptions = {}): void {
    const topo = this.topo;
    if (!topo) throw new Error('ArapCore.solve called before bind');
    const restPos = topo.restPos;
    const cl = this.constrainedList;

    // Capture constraint targets (the lerp below overwrites pos[constrained]).
    for (let c = 0; c < cl.length; c++) {
      const i = cl[c];
      this.cTargetX[c] = this.pos[3 * i];
      this.cTargetY[c] = this.pos[3 * i + 1];
      this.cTargetZ[c] = this.pos[3 * i + 2];
    }

    this.setAllIdentity();
    const steps = Math.max(1, options.loadSteps ?? 1);
    const iters = Math.max(1, options.arapIters ?? 10);

    for (let s = 1; s <= steps; s++) {
      const a = s / steps;
      for (let c = 0; c < cl.length; c++) {
        const i = cl[c];
        this.pos[3 * i] = restPos[3 * i] + (this.cTargetX[c] - restPos[3 * i]) * a;
        this.pos[3 * i + 1] = restPos[3 * i + 1] + (this.cTargetY[c] - restPos[3 * i + 1]) * a;
        this.pos[3 * i + 2] = restPos[3 * i + 2] + (this.cTargetZ[c] - restPos[3 * i + 2]) * a;
      }
      this.globalSolve();
      if (this.useRotations) {
        for (let it = 0; it < iters; it++) {
          this.computeRotations();
          this.globalSolve();
        }
      }
    }
  }

  // ----- internals -------------------------------------------------------------

  private globalSolve(): void {
    this.buildRhs();
    this.loadFreeAxis(0, this.solX);
    this.loadFreeAxis(1, this.solY);
    this.loadFreeAxis(2, this.solZ);
    this.solver.solve(this.rhsX, this.solX, this.solX);
    this.solver.solve(this.rhsY, this.solY, this.solY);
    this.solver.solve(this.rhsZ, this.solZ, this.solZ);
    this.storeFreeAxes();
  }

  private buildFreeFreeMatrix(regularization: number): SparseCsr {
    const topo = this.topo as ArapNumericTopology;
    const fl = this.freeListArr;
    const nFree = fl.length;

    let meanDiag = 0;
    for (let fi = 0; fi < nFree; fi++) meanDiag += Math.abs(topo.diag[fl[fi]]);
    meanDiag = nFree > 0 ? meanDiag / nFree : 1;
    const lambda = regularization * (meanDiag > 0 ? meanDiag : 1);

    const rowPtr = new Int32Array(nFree + 1);
    for (let fi = 0; fi < nFree; fi++) {
      const gi = fl[fi];
      let count = 1;
      for (let k = topo.rowPtr[gi]; k < topo.rowPtr[gi + 1]; k++) {
        if (this.freeIndexOf[topo.colIdx[k]] >= 0) count++;
      }
      rowPtr[fi + 1] = rowPtr[fi] + count;
    }
    const nnz = rowPtr[nFree];
    const colIdx = new Int32Array(nnz);
    const values = new Float64Array(nnz);
    for (let fi = 0; fi < nFree; fi++) {
      const gi = fl[fi];
      let cursor = rowPtr[fi];
      colIdx[cursor] = fi;
      values[cursor] = topo.diag[gi] + lambda;
      cursor++;
      for (let k = topo.rowPtr[gi]; k < topo.rowPtr[gi + 1]; k++) {
        const fj = this.freeIndexOf[topo.colIdx[k]];
        if (fj >= 0) {
          colIdx[cursor] = fj;
          values[cursor] = -topo.weight[k];
          cursor++;
        }
      }
    }
    return { n: nFree, rowPtr, colIdx, values };
  }

  private buildRhs(): void {
    const topo = this.topo as ArapNumericTopology;
    const { restPos, rowPtr, colIdx, weight } = topo;
    const rot = this.rot;
    const pos = this.pos;
    const fl = this.freeListArr;

    for (let fi = 0; fi < fl.length; fi++) {
      const i = fl[fi];
      let bx = 0;
      let by = 0;
      let bz = 0;
      const ix = restPos[3 * i];
      const iy = restPos[3 * i + 1];
      const iz = restPos[3 * i + 2];
      const ri = 9 * i;
      for (let k = rowPtr[i]; k < rowPtr[i + 1]; k++) {
        const j = colIdx[k];
        const w = weight[k];
        const ex = ix - restPos[3 * j];
        const ey = iy - restPos[3 * j + 1];
        const ez = iz - restPos[3 * j + 2];
        const rj = 9 * j;
        const sx = (rot[ri] + rot[rj]) * ex + (rot[ri + 1] + rot[rj + 1]) * ey + (rot[ri + 2] + rot[rj + 2]) * ez;
        const sy = (rot[ri + 3] + rot[rj + 3]) * ex + (rot[ri + 4] + rot[rj + 4]) * ey + (rot[ri + 5] + rot[rj + 5]) * ez;
        const sz = (rot[ri + 6] + rot[rj + 6]) * ex + (rot[ri + 7] + rot[rj + 7]) * ey + (rot[ri + 8] + rot[rj + 8]) * ez;
        const half = 0.5 * w;
        bx += half * sx;
        by += half * sy;
        bz += half * sz;
        if (this.isConstrained[j] === 1) {
          bx += w * pos[3 * j];
          by += w * pos[3 * j + 1];
          bz += w * pos[3 * j + 2];
        }
      }
      this.rhsX[fi] = bx;
      this.rhsY[fi] = by;
      this.rhsZ[fi] = bz;
    }
  }

  private computeRotations(): void {
    const topo = this.topo as ArapNumericTopology;
    const { restPos, rowPtr, colIdx, weight } = topo;
    const pos = this.pos;
    const cov = this.cov;
    for (let i = 0; i < topo.n; i++) {
      cov.fill(0);
      const irx = restPos[3 * i];
      const iry = restPos[3 * i + 1];
      const irz = restPos[3 * i + 2];
      const icx = pos[3 * i];
      const icy = pos[3 * i + 1];
      const icz = pos[3 * i + 2];
      for (let k = rowPtr[i]; k < rowPtr[i + 1]; k++) {
        const j = colIdx[k];
        const w = weight[k];
        const erx = irx - restPos[3 * j];
        const ery = iry - restPos[3 * j + 1];
        const erz = irz - restPos[3 * j + 2];
        const ecx = icx - pos[3 * j];
        const ecy = icy - pos[3 * j + 1];
        const ecz = icz - pos[3 * j + 2];
        cov[0] += w * erx * ecx; cov[1] += w * erx * ecy; cov[2] += w * erx * ecz;
        cov[3] += w * ery * ecx; cov[4] += w * ery * ecy; cov[5] += w * ery * ecz;
        cov[6] += w * erz * ecx; cov[7] += w * erz * ecy; cov[8] += w * erz * ecz;
      }
      fitRotation(cov, this.rot.subarray(9 * i, 9 * i + 9));
    }
  }

  private setAllIdentity(): void {
    const n = this.topo ? this.topo.n : 0;
    const rot = this.rot;
    for (let i = 0; i < n; i++) {
      const r = 9 * i;
      rot[r] = 1; rot[r + 1] = 0; rot[r + 2] = 0;
      rot[r + 3] = 0; rot[r + 4] = 1; rot[r + 5] = 0;
      rot[r + 6] = 0; rot[r + 7] = 0; rot[r + 8] = 1;
    }
  }

  private loadFreeAxis(axis: 0 | 1 | 2, out: Float64Array): void {
    const fl = this.freeListArr;
    for (let fi = 0; fi < fl.length; fi++) out[fi] = this.pos[3 * fl[fi] + axis];
  }

  private storeFreeAxes(): void {
    const fl = this.freeListArr;
    for (let fi = 0; fi < fl.length; fi++) {
      const gi = fl[fi];
      this.pos[3 * gi] = this.solX[fi];
      this.pos[3 * gi + 1] = this.solY[fi];
      this.pos[3 * gi + 2] = this.solZ[fi];
    }
  }
}
